import { InvoiceLineItem } from 'src/redux/billing/billing.types';
import { BillingFilterType } from '../types/billing.types';

/**
 * Format currency amount
 */
export const formatCurrency = (amount: number, currency: string = 'USD', minimumFractionDigits: number = 2, maximumFractionDigits: number = 2): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: minimumFractionDigits,
    maximumFractionDigits: maximumFractionDigits,
  }).format(amount);
};

/**
 * Format date for display
 */
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Format date and time for display
 */
export const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Format date and time for display
 */
export const formatDateTimeVerbose = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffMin < 1) {
    return 'Just now';
  }
  if (diffMin < 60) {
    return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
  }
  if (diffHr < 24) {
    return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`;
  }
  if (diffDay < 7) {
    return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
  }
  // If it's more than a week ago, display just the date (no time)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Validate billing filter type
 */
export const isValidBillingFilter = (filter: string): filter is BillingFilterType => {
  return filter === 'insertion-orders' || filter === 'invoices';
};

/**
 * Get display name for billing filter
 */
export const getBillingFilterDisplayName = (filter: BillingFilterType): string => {
  switch (filter) {
    case 'insertion-orders':
      return 'Insertion Orders';
    case 'invoices':
      return 'Invoices';
    default:
      return 'Unknown';
  }
};

/**
 * Generate unique key for billing items
 */
export const generateBillingItemKey = (prefix: string, id: string): string => {
  return `${prefix}-${id}`;
};

/**
 * Debounce function for search
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

export const fieldValue = (lineItem: InvoiceLineItem, field: string) => {
  if (`${field}_override` in lineItem) {
    // is the override value set or is it null/blank/undefined?
    if (lineItem[`${field}_override` as keyof InvoiceLineItem] !== null && lineItem[`${field}_override` as keyof InvoiceLineItem] !== undefined && lineItem[`${field}_override` as keyof InvoiceLineItem] !== '') {
      return lineItem[`${field}_override` as keyof InvoiceLineItem]?.toString();
    }
  }
  return lineItem[field as keyof InvoiceLineItem]?.toString();
};